import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatCurrency } from '../../lib/utils';
import { supabase } from '../../lib/supabase';
import { Eye, FileText, ShoppingBag, Search, Loader2, ExternalLink } from 'lucide-react';
import { OrderDetailDrawer } from './OrderDetailDrawer';
interface OrdersHistoryTableProps {
  orders: any[];
  orderItems: any[];
  restaurantId: string | number | undefined;
  currencySymbol?: string;
}
const EXCLUDED_STATUSES = new Set(['cancelled', 'canceled', 'refunded', 'rejected']);
const STATUS_LABEL: Record<string, string> = {
  new: 'Nuevo',
  pending: 'Pendiente',
  accepted: 'Aceptado',
  preparing: 'En preparación',
  ready: 'Listo',
  searching_driver: 'Buscando conductor',
  out_for_delivery: 'En camino',
  delivered: 'Entregado',
  paid: 'Pagado',
  closed: 'Cerrado',
  completed: 'Completado'
};
const STATUS_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'default'> = {
  delivered: 'success',
  paid: 'success',
  closed: 'success',
  completed: 'success',
  ready: 'success',
  preparing: 'info',
  accepted: 'info',
  out_for_delivery: 'info',
  searching_driver: 'warning',
  new: 'warning',
  pending: 'warning'
};
const CHANNEL_LABEL: Record<string, string> = {
  web: 'Marketplace',
  qr: 'QR / Mesa',
  waiter: 'Mesero',
  pos: 'POS',
  whatsapp: 'WhatsApp'
};
export function OrdersHistoryTable({
  orders,
  orderItems,
  restaurantId,
  currencySymbol = '₡'
}: OrdersHistoryTableProps) {
  const [search, setSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState<string | null>(null);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);
  // Index items by order_id for quick lookup
  const itemsByOrder = useMemo(() => {
    const map = new Map<string | number, any[]>();
    for (const item of orderItems) {
      const arr = map.get(item.order_id) || [];
      arr.push(item);
      map.set(item.order_id, arr);
    }
    return map;
  }, [orderItems]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter(o => !EXCLUDED_STATUSES.has(String(o.status || '').toLowerCase())).filter(o => {
      if (!term) return true;
      const hay = [o.order_number, o.customer_name, o.customer_phone, o.customer_email].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(term);
    }).sort((a, b) => a.created_at < b.created_at ? 1 : -1);
  }, [orders, search]);
  const totalRevenue = useMemo(() => filtered.reduce((sum, o) => sum + (Number(o.total) || 0), 0), [filtered]);
  const handleGenerateInvoice = async (orderId: string | number) => {
    setInvoiceLoadingId(String(orderId));
    setInvoiceError(null);
    try {
      const {
        data,
        error
      } = await supabase.from('payments').select('id').eq('order_id', orderId).order('created_at', {
        ascending: false
      }).limit(1).maybeSingle();
      if (error) throw error;
      if (!data?.id) {
        setInvoiceError('Este pedido aún no se ha cobrado en Caja. Cobra primero para generar la factura.');
        return;
      }
      window.open(`/factura/${data.id}`, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('[OrdersHistoryTable] invoice error:', err);
      setInvoiceError('No se pudo generar la factura. Intenta de nuevo.');
    } finally {
      setInvoiceLoadingId(null);
    }
  };
  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('es-CR', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <>
      <Card data-id="element-1400" data-mp="xva3hirr8iul">
        <CardHeader data-id="element-1401" data-mp="xvjqqylf0bwl">
          <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-1402" data-mp="xwbejn8rt5cb">
            <div data-id="element-1403" data-mp="12tv3bh33h0ii">
              <CardTitle className="flex items-center gap-2" data-id="element-1404" data-mp="12vfls8c4wt00">
                <ShoppingBag className="w-5 h-5 text-brand-primary" data-id="element-1405" data-mp="16tvfsw6mv1mx" />
                Histórico de Pedidos
              </CardTitle>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1406" data-mp="1bvazokx2r23q">
                {filtered.length} pedidos · Total{' '}
                {formatCurrency(totalRevenue, currencySymbol)}{' '}
                <span className="text-gray-400" data-id="element-1407" data-mp="1lqmj3gjfgxht">
                  (no incluye pedidos cancelados)
                </span>
              </p>
            </div>
            <div className="relative w-full sm:w-72" data-id="element-1408" data-mp="1uk9bcwoffim9">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-1409" data-mp="1ujps9cn1io6n" />
              <input type="text" placeholder="Buscar por pedido, cliente, teléfono..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary" data-id="element-1410" data-mp="1zlf6f15k7hyy" />
            </div>
          </div>
        </CardHeader>
        <CardContent data-id="element-1411" data-mp="20nmal1c1azns">
          {invoiceError && <div className="mb-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-start justify-between gap-3" data-id="element-1412" data-mp="20oxgp7sycji2">
              <span data-id="element-1413" data-mp="2sdqs8s8et8lb">{invoiceError}</span>
              <button onClick={() => setInvoiceError(null)} className="text-amber-500 hover:text-amber-700 font-bold" data-id="element-1414" data-mp="2sescnnbbf7fp">
                ✕
              </button>
            </div>}

          {filtered.length === 0 ? <div className="text-center py-8 text-gray-500 text-sm" data-id="element-1415" data-mp="3g4ec8bvlnoo8">
              {search ? 'No se encontraron pedidos con esa búsqueda.' : 'No hay pedidos en el rango seleccionado.'}
            </div> : <div className="overflow-x-auto -mx-6" data-id="element-1416" data-mp="1eefhp5gjmzfv">
              <table className="w-full text-sm text-left min-w-[900px]" data-id="element-1417" data-mp="1edw0xs2mqh3f">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50" data-id="element-1418" data-mp="zqax1vxxkdug">
                  <tr data-id="element-1419" data-mp="14oci2mex2n8k">
                    <th className="px-6 py-3 rounded-l-lg" data-id="element-1420" data-mp="14nkk54xukdf4">Pedido</th>
                    <th className="px-4 py-3" data-id="element-1421" data-mp="qp4frkngiz05">Fecha</th>
                    <th className="px-4 py-3" data-id="element-1422" data-mp="qn9w09nx9cxn">Cliente</th>
                    <th className="px-4 py-3" data-id="element-1423" data-mp="voh3s8q2ec4b">Canal</th>
                    <th className="px-4 py-3" data-id="element-1424" data-mp="vluv25rftqki">Estado</th>
                    <th className="px-4 py-3" data-id="element-1425" data-mp="grqc6lmmu4p9">Items</th>
                    <th className="px-4 py-3 text-right rounded-r-lg" data-id="element-1426" data-mp="lprtb10hzty3">Total</th>
                    {/* Columna "Acciones" (Detalle / Factura) oculta temporalmente
                     a pedido del cliente. La lógica se conserva intacta para
                     re-activarla luego sin romper nada. */}
                  </tr>
                </thead>
                <tbody data-id="element-1427" data-mp="4hh1laxz51rw">
                  {filtered.map(o => {
                const items = itemsByOrder.get(o.id) || [];
                const itemCount = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
                const channel = CHANNEL_LABEL[String(o.source || '').toLowerCase()] || o.source || '—';
                const statusKey = String(o.status || '').toLowerCase();
                return <tr key={o.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50" data-id="element-1428" data-mp="x5mni10rkwtb">
                        <td className="px-6 py-3 font-mono text-xs text-gray-700" data-id="element-1429" data-mp="2ywg7d0hsc074">
                          {o.order_number || `#${o.id}`}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap" data-id="element-1430" data-mp="2tz7thc3o6ibo">
                          {formatDate(o.created_at)}
                        </td>
                        <td className="px-4 py-3" data-id="element-1431" data-mp="2d2907o8uxxa4">
                          <div className="font-medium text-gray-900" data-id="element-1432" data-mp="2d0xyrv2vx1m6">
                            {o.customer_name || 'Sin nombre'}
                          </div>
                          {o.customer_phone && <div className="text-xs text-gray-500" data-id="element-1433" data-mp="1p5lgfpjtu8v9">
                              {o.customer_phone}
                            </div>}
                        </td>
                        <td className="px-4 py-3 text-gray-600" data-id="element-1434" data-mp="1z2zvplu0krlz">{channel}</td>
                        <td className="px-4 py-3" data-id="element-1435" data-mp="242m8ju5xk5u9">
                          <Badge variant={STATUS_VARIANT[statusKey] || 'default'} data-id="element-1436" data-mp="243du0c5tcn6t">
                            {STATUS_LABEL[statusKey] || o.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-gray-600" data-id="element-1437" data-mp="13uxlpitxxwvz">
                          {itemCount}{' '}
                          {itemCount === 1 ? 'producto' : 'productos'}
                        </td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900 whitespace-nowrap" data-id="element-1438" data-mp="u431ek0997d6">
                          {formatCurrency(o.total, currencySymbol)}
                        </td>
                        {/* Celda "Acciones" (Detalle / Factura) oculta temporalmente
                         a pedido del cliente. handleGenerateInvoice y el drawer
                         de detalle se conservan para re-activar luego. */}
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </CardContent>
      </Card>

      <OrderDetailDrawer isOpen={!!selectedOrderId} onClose={() => setSelectedOrderId(null)} orderId={selectedOrderId} data-id="element-1439" data-mp="25igin60i6ok8" />
    </>;
}